import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FileText, MoreVertical } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { Button } from 'src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { getActionComponent } from '../actions';
import { getActionScheme } from '../actions/actionColors';
import { useSelectionContext } from './SelectionContext';
import {
  statusLabel,
  statusBgClass,
  statusIcon,
  measureStatusLabelWidth,
} from './tabFormatters';

// Trailing row-actions cell — primary action / kebab. "View profile" used
// to be an always-visible button here; the backend's flow-statuses response
// now flags at most one manual_action per status as `primary_action: true`
// (per-row, not per-key — the same action key can be primary in one status
// and not in another, since it's driven by that status's own conditions),
// and the request is to show THAT action outside instead, moving "View
// profile" into the kebab alongside every other non-primary manual action.
// A status with no primary_action (e.g. "Test In Progress", "In Progress")
// leaves the primary slot with nothing to put there EXCEPT "View profile" —
// confirmed live this read as a broken-looking gap next to a still-present
// kebab, so "View profile" fills that slot instead whenever the backend
// hasn't flagged a real primary_action (see `viewProfileIsPrimary` below),
// rather than leaving it empty. "View profile" still opens
// CandidateProfileModal in place (owned by AssignmentPipeline.jsx, one
// instance shared by every tab — see onViewProfile below) rather than
// navigating to a full page. The rest of the kebab lists whichever manual
// (state-transition) actions the row's own status carries, resolved the
// exact same way ManualActionsBar resolves them for the bulk bar
// (stateMachineKey + action key → actions/actionRegistry.js), just scoped to
// this one candidate instead of a multi-select. A status with no manual
// actions at all (e.g. Shortlisted — see SeniorShortlistedTab.jsx) has
// nothing left for a kebab at all once "View profile" moves to the primary
// slot, so there's no kebab there either (see `hasKebab` below). (A
// row-expand chevron used to sit here too, but there's no expand content
// defined for this table yet, so it was removed rather than kept as a dead,
// always-disabled-looking button.)
//
// `rowStatus` (distinct from `status` below, which is a tab-fixed value
// baked into each action's own fire payload) is the row's own real status —
// only passed by callers in a merged "All statuses" view (e.g.
// seniorFlowTabFactory.jsx's `showStatus: !selectable`-equivalent gate).
// When present it renders pinned to the top-right corner of this cell —
// since row_actions is always the last column, that corner reads as the
// row's own top-right corner. Absolutely positioned (the row_actions
// column's own `className: 'relative'` makes its <td> the containing
// block — see seniorFlowTabFactory.jsx/SeniorContactedTab.jsx) rather than
// stacked inline, so it doesn't compete with the button row for width or
// push rows taller; `pt-4` on the button row below just leaves clear room
// under it. This used to live inline next to the candidate's name
// (CandidateCell.jsx), then stacked above the buttons in normal flow — both
// read as cluttered per explicit feedback; pinning it to the corner instead
// reads as a passive status marker rather than fighting the buttons for
// attention.
const RowActionsCell = ({
  candidateId,
  mappingId,
  status,
  rowStatus,
  opportunityId,
  assignmentId,
  requirementLocationId,
  stateMachineKey,
  manualActions,
  onActionSuccess,
  onViewProfile,
}) => {
  // Which resolved action's payload modal is open, or null. Lives here —
  // not inside any individual action component — because the trigger
  // (DropdownMenuItem, inside the dropdown) unmounts the instant the
  // dropdown closes on click; a modal owned by that same instance would
  // unmount with it before ever showing. See GenericFlowAction.jsx's
  // renderAs="modalOnly" comment for the full story.
  const [openModalKey, setOpenModalKey] = useState(null);
  // Once any row is checkbox-selected, the bulk bar at the bottom is the
  // one place showing whatever actions apply to the current selection —
  // every row's own kebab hides itself (not just this row's) rather than
  // offering a second, redundant way to act on a single candidate at the
  // same time. Payload actions (assign_slot etc.) are deliberately absent
  // from the bulk bar (see ManualActionsBar.jsx), so they're reachable
  // only through this kebab, i.e. only while nothing is selected.
  const { hasSelection } = useSelectionContext();

  const handleViewProfile = (e) => {
    e.stopPropagation();
    onViewProfile(candidateId);
  };

  const resolvedActions = manualActions
    .map((action) => ({
      ...action,
      Component: getActionComponent(stateMachineKey, action.key),
    }))
    .filter((action) => action.Component);

  // At most one resolved action is flagged primary for this row's current
  // status — render it as the outside button in place of the old hardcoded
  // "View profile", and keep it out of the kebab list below so it isn't
  // offered twice.
  const primaryAction = resolvedActions.find((action) => action.primary_action);
  const secondaryActions = resolvedActions
    .filter((action) => action !== primaryAction)
    // Destructive (red-scheme) actions sort to the bottom of the kebab,
    // just above "View profile" — per explicit request, so Reject/Mark as
    // No Show/etc. don't sit interleaved among neutral ones. A stable sort
    // (guaranteed by spec) keeps every other action in its original
    // (backend-given) order, only red ones move, relative to each other
    // unchanged too.
    .sort(
      (a, b) =>
        (getActionScheme(a.key) === 'red' ? 1 : 0) -
        (getActionScheme(b.key) === 'red' ? 1 : 0)
    );
  // No real primary_action for this row → "View profile" takes that slot
  // instead (see file header comment), which also means it has nowhere left
  // to duplicate into inside the kebab.
  const viewProfileIsPrimary = !primaryAction;
  // A kebab that would only ever contain "View profile" (e.g. Shortlisted,
  // whose manual_actions is genuinely empty — see SeniorShortlistedTab.jsx,
  // or any other status with secondary actions but View profile already
  // sitting in the primary slot) isn't worth the extra click — only render
  // one when there's a real primary_action to host "View profile" instead
  // (kebabNeedsViewProfile) and/or real secondary actions to list.
  const kebabNeedsViewProfile = !viewProfileIsPrimary;
  const hasKebab = secondaryActions.length > 0 || kebabNeedsViewProfile;

  // sec_mapping_id is what flow-actions fires against — distinct from
  // candidateId (used above for View profile, which navigates by the
  // candidate's own identity). Falls back to candidateId only so a caller
  // that hasn't been updated to pass mappingId yet doesn't crash — with no
  // resolvedActions that fallback is never actually used to fire anything.
  const effectiveMappingId = mappingId ?? candidateId;

  // Collapsed to just the icon at rest (w-5), pinned to the cell's top-right
  // corner — same "icon-only chip, hover to reveal" treatment as
  // Opportunities.jsx's "forwarded to TA" badge, mirrored for the opposite
  // corner. The container is `right-0` (its right edge is the fixed anchor;
  // width grows by extending the LEFT edge further left), icon first +
  // `justify-start` so the icon sits flush against that moving left edge —
  // it visibly translates left as the chip expands — with the label
  // immediately after it in normal flow, clipped by `overflow-hidden` until
  // there's enough width for it, so it reveals left-to-right (its own start
  // first) right behind the icon as the icon moves out of the way. One
  // `transition-[width]` drives both, no separate transform/delay needed.
  // Expanded width is per-label and content-fit (`--rs-w`) rather than one
  // fixed value sized for the longest label — a short label like "New" or
  // "Rejected" no longer drags along a big empty chip. Sized off an actual
  // canvas measurement of the label text (see measureStatusLabelWidth) plus
  // a fixed px allowance for the icon/gap/padding — a `ch`-based guess was
  // tried first but left a visible gap after longer labels (ch approximates
  // off the "0" glyph, not this font's real, narrower character widths).
  const StatusIcon = rowStatus ? statusIcon(rowStatus) : null;
  const statusLabelText = rowStatus ? statusLabel(rowStatus) : '';
  const statusExpandedWidth = rowStatus
    ? Math.ceil(measureStatusLabelWidth(statusLabelText)) + 24
    : 0;

  return (
    <div data-prevent-row-click="true">
      {rowStatus && StatusIcon && (
        <span
          style={{ '--rs-w': `${statusExpandedWidth}px` }}
          className={cn(
            'absolute top-0 right-0 z-10 flex h-5 w-5 cursor-default select-none items-center justify-start gap-1 overflow-hidden whitespace-nowrap rounded-bl-sm px-1.5 pr-0 text-[8px] font-semibold text-white transition-[width,box-shadow] duration-300 ease-in-out hover:w-[var(--rs-w)] hover:shadow-md',
            statusBgClass(rowStatus)
          )}
        >
          <StatusIcon className="h-2.5 w-2.5 flex-shrink-0" strokeWidth={2.5} />
          <span className="truncate">{statusLabelText}</span>
        </span>
      )}
      <div
        className={cn(
          'flex items-center justify-end gap-1.5',
          rowStatus && 'pt-4'
        )}
      >
        {primaryAction && (
          <primaryAction.Component
            actionKey={primaryAction.key}
            label={primaryAction.label}
            mappingIds={[effectiveMappingId]}
            candidateIds={[candidateId]}
            opportunityId={opportunityId}
            assignmentId={assignmentId}
            requirementLocationId={requirementLocationId}
            status={status}
            onSuccess={onActionSuccess}
            renderAs="button"
          />
        )}
        {!hasSelection && viewProfileIsPrimary && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs"
            onClick={handleViewProfile}
          >
            <FileText className="h-3.5 w-3.5" />
            View profile
          </Button>
        )}
        {!hasSelection && hasKebab && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-6 w-6 py-3 rounded-sm"
                onClick={(e) => e.stopPropagation()}
                aria-label="More actions"
              >
                <MoreVertical className="h-4 w-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(e) => e.stopPropagation()}
            >
              {secondaryActions.map(({ key, label, Component }) => (
                <Component
                  key={key}
                  actionKey={key}
                  label={label}
                  mappingIds={[effectiveMappingId]}
                  candidateIds={[candidateId]}
                  opportunityId={opportunityId}
                  assignmentId={assignmentId}
                  requirementLocationId={requirementLocationId}
                  status={status}
                  onSuccess={onActionSuccess}
                  renderAs="menuItem"
                  onModalOpenChange={(open) =>
                    setOpenModalKey(open ? key : null)
                  }
                />
              ))}
              {kebabNeedsViewProfile && (
                <>
                  {secondaryActions.length > 0 && <DropdownMenuSeparator />}
                  <DropdownMenuItem
                    onClick={handleViewProfile}
                    className="gap-2 text-muted-foreground focus:text-foreground-strong"
                  >
                    <FileText className="h-4 w-4" />
                    View profile
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {/* Modal host, deliberately a sibling of <DropdownMenu> above rather
            than nested inside it — see the state comment and
            GenericFlowAction.jsx's renderAs="modalOnly" comment. Every
            non-primary resolved action gets an instance here — a payload
            form for the few that need structured input, a plain
            confirmation for everything else (see GenericFlowAction's
            handleClick). The primary action renders via renderAs="button"
            above instead, which owns its own modal state and needs no host
            here. */}
        {!hasSelection &&
          secondaryActions.map(({ key, label, Component }) => (
            <Component
              key={`modal-${key}`}
              actionKey={key}
              label={label}
              mappingIds={[effectiveMappingId]}
              candidateIds={[candidateId]}
              opportunityId={opportunityId}
              assignmentId={assignmentId}
              requirementLocationId={requirementLocationId}
              status={status}
              onSuccess={onActionSuccess}
              renderAs="modalOnly"
              modalOpen={openModalKey === key}
              onModalOpenChange={(open) => setOpenModalKey(open ? key : null)}
            />
          ))}
      </div>
    </div>
  );
};

RowActionsCell.propTypes = {
  candidateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  mappingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  status: PropTypes.string,
  rowStatus: PropTypes.string,
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  requirementLocationId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  stateMachineKey: PropTypes.string,
  manualActions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      primary_action: PropTypes.bool,
    })
  ),
  onActionSuccess: PropTypes.func,
  // Opens CandidateProfileModal for this candidateId — owned by
  // AssignmentPipeline.jsx, one instance shared across every tab.
  // .isRequired: RowActionsCell only ever mounts inside AssignmentPipeline's
  // own tab tree, which always supplies it.
  onViewProfile: PropTypes.func.isRequired,
};

RowActionsCell.defaultProps = {
  mappingId: undefined,
  status: undefined,
  rowStatus: undefined,
  opportunityId: undefined,
  assignmentId: undefined,
  requirementLocationId: undefined,
  stateMachineKey: 'default',
  manualActions: [],
  onActionSuccess: () => {},
};

export default RowActionsCell;
